import { Size, Variations } from "@utilities/types";
import React, {
  forwardRef,
  useState,
  useCallback,
  useImperativeHandle,
  useMemo,
  memo
} from "react";
import { Input } from "./Input";

export interface SizePickerRef {
  resetSizes: () => void;
}

interface SizePickerProps {
  onChange: (sizes: string[] | Size[]) => void;
  initialVariation?: Variations | null;
  initialSizesList?: Size[];
  withQuantity?: boolean;
}

const availableSizes = [
  "2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL",
  ...Array.from({ length: 101 }, (_, i) => i.toString())
];

const SizePickerBase = forwardRef<SizePickerRef, SizePickerProps>(
  ({ onChange, initialVariation, initialSizesList, withQuantity = false }, ref) => {
    
    const [selectedSize, setSelectedSize] = useState("");
    const [sizes, setSizes] = useState<Size[]>(
      // @ts-ignore
      initialVariation ? initialVariation.sizes 
      : initialSizesList ? initialSizesList : []
    );

    const handleAddSize = useCallback(() => {
      if (selectedSize && !sizes.some(s => s.size === selectedSize)) {
        const newSizes = [...sizes, { size: selectedSize, quantity: withQuantity ? 0 : undefined }];
        setSizes(newSizes);
        onChange(withQuantity ? newSizes : newSizes.map(s => s.size));
      }
    }, [selectedSize, sizes, onChange, withQuantity]);

    const handleRemoveSize = useCallback((sizeToRemove: string) => {
      const newSizes = sizes.filter((s) => s.size !== sizeToRemove);
      setSizes(newSizes);
      onChange(withQuantity ? newSizes : newSizes.map(s => s.size));
    }, [sizes, onChange, withQuantity]);

    const handleQuantityChange = useCallback((size: string, quantity: number) => {
      const updatedSizes = sizes.map(s =>
        s.size === size ? { ...s, quantity } : s
      );
      setSizes(updatedSizes);
      onChange(updatedSizes);
    }, [sizes, onChange]);

    const resetSizes = useCallback(() => {
      setSizes([]);
      setSelectedSize("");
      onChange([]);
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      resetSizes
    }), [resetSizes]);

    const sizeOptions = useMemo(() => (
      availableSizes.map((size) => ({ value: size, label: size }))
    ), []);

    return (
      <div className="w-full p-4 border rounded-lg shadow-md bg-white">
        <label className="block text-gray-700 font-semibold mb-2">Tailles disponibles</label>
        <div className="flex items-center gap-2 mb-3">
          <Input
            type="select"
            placeholder="Sélectionner une taille"
            className="select select-bordered w-full"
            value={selectedSize}
            onChange={(e) => setSelectedSize(e.target.value)}
            options={sizeOptions}
          />
          <button
            onClick={handleAddSize}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600 transition"
          >
            Ajouter
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {sizes.map(({ size, quantity }, index) => (
            <div key={index} className="flex items-center gap-2 p-2 border rounded-lg shadow-sm bg-gray-100">
              <span className="text-gray-700 text-sm font-medium">{size}</span>
              {withQuantity && (
                <Input
                  type="number"
                  min={0}
                  className="input input-bordered w-16"
                  value={quantity || 0}
                  onChange={(e) => handleQuantityChange(size, parseInt(e.target.value) || 0)}
                />
              )}
              <button
                onClick={() => handleRemoveSize(size)}
                className="text-red-500 hover:text-red-700 px-2 py-1 border border-red-300 rounded-lg"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

// ✅ Mémoïsation complète du composant
export const SizePicker = memo(SizePickerBase);
